import React from 'react';
import { Form, Input, Radio, Row, Col } from "antd";
import moment from "moment";
import { AvatarUpload } from './AvatarUpload';
import { UserCompanyFormItem } from './UserCompanyFormItem';
import { AssigningFacilityFormItem } from './AssigningFacilityFormItem';
import { UserRoleFormItem } from './UserRoleFormItem';
import { PermissionProfileFormItem } from './PermissionProfileFormItem';
import { CONTRACT_USER_ROLE } from '../utils';

const UserDetailFormFields = ({ disabled, isCreate, defaultImage }) => {

    const role = Form.useWatch('role');
    const createdBy = Form.useWatch('createdby_name');
    const createdDate = Form.useWatch('created_date');
    const modifiedBy = Form.useWatch('lastModified_name');
    const modifiedDate = Form.useWatch('modified_date');
    const isCreatingContractUser = isCreate && role === CONTRACT_USER_ROLE;
    const needAssigningFacilityRequired = !(role === 1 || role === 3 || isCreatingContractUser);
    const renderAuditItem = (label, value) => (
        <Form.Item>
            <div style={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
                <span style={{ flex: '0 0 110px', color: 'rgba(0, 0, 0, 0.85)' }}>{label}:</span>
                <span>{value || ''}</span>
            </div>
        </Form.Item>
    );

    return (
        <Row gutter={24}>
            <Col xs={24}>
                <Form.Item
                    label="Avatar"
                    name="image"
                >
                    <AvatarUpload key={disabled + defaultImage} disabled={disabled} defaultImage={defaultImage} />
                </Form.Item>
            </Col>

            <Col xs={24} md={12}>
                <Form.Item
                    label="Username"
                    name="username"
                    required
                    rules={[{ required: true, message: 'Please input username!' }]}
                >
                    <Input />
                </Form.Item>
            </Col>

            <Col xs={24} md={12}>
                <Form.Item
                    label="Email"
                    name="email"
                    required
                    rules={[{ required: true, message: 'Please input email!' }, {
                        pattern: /^[a-zA-Z0-9.+]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,63}$/,
                        message: 'Email can only contain letters (a-z), numbers (0-9), dots (.), and plus signs (+)'
                    }]}
                >
                    <Input />
                </Form.Item>
            </Col>

            {isCreate && <>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Password"
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: 'Please input your password!',
                            },
                            {
                                min: 6,
                                message: 'Password must be at least 6 characters',
                            }
                        ]}
                    >
                        <Input.Password
                            size="large"
                            type="password"
                            placeholder="Password"
                            autoComplete="false"
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Confirm Password"
                        name="verify_password"
                        dependencies={['password']}
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm your password!',
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The two passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            size="large"
                            type="password"
                            placeholder="Password"
                            autoComplete="false"
                        />
                    </Form.Item>
                </Col>
            </>
            }



            <Col xs={24} md={12}>
                <Form.Item
                    label="First Name"
                    name="first_name"
                    rules={[{ required: true, message: 'Please input first name!' }]}
                >
                    <Input />
                </Form.Item>
            </Col>

            {/* <Col xs={24} md={12}>
                <Form.Item
                    label="Middle Name"
                    name="middle_name"
                >
                    <Input />
                </Form.Item>
            </Col> */}

            <Col xs={24} md={12}>
                <Form.Item
                    label="Last Name"
                    name="last_name"
                    rules={[{ required: true, message: 'Please input last name!' }]}
                >
                    <Input />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <UserCompanyFormItem />
            </Col>
            <Col xs={24} md={12}>
                <Form.Item
                    label="Status"
                    name="is_active"
                    rules={[{ required: true, message: 'Please select status!' }]}
                >
                    <Radio.Group>
                        <Radio value={true}>Active</Radio>
                        <Radio value={false}>Disable</Radio>
                    </Radio.Group>
                </Form.Item>
            </Col>



            <Col xs={24}>
                <Form.Item
                    label="Role"
                    name="role"
                    rules={[{ required: true, message: 'Please select role!' }]}
                >
                    <UserRoleFormItem disabled={disabled} />
                </Form.Item>
            </Col>
            
            <Col xs={12}>
                <PermissionProfileFormItem />
            </Col>

            {!isCreatingContractUser && (
                <Col xs={24}>
                    <Form.Item
                        label="Assigning Company and Facility"
                        name="user"
                        required={needAssigningFacilityRequired}
                        dependencies={['role', 'company']}
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const selectedRole = getFieldValue('role');
                                    const isRequired = !(selectedRole === 1 || selectedRole === 3 || (isCreate && selectedRole === CONTRACT_USER_ROLE));
                                    if (!isRequired || value?.length > 0) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Please select at least one facility!'));
                                },
                            }),
                        ]}
                    >
                        <AssigningFacilityFormItem disabled={disabled} />
                    </Form.Item>
                </Col>
            )}

            {!isCreate && (
                <>
                    <Col xs={24} md={12}>
                        {renderAuditItem('Created By', createdBy)}
                    </Col>
                    <Col xs={24} md={12}>
                        {renderAuditItem(
                            'Created Date',
                            createdDate ? moment(createdDate).format('YYYY-MM-DD HH:mm') : ''
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        {renderAuditItem('Modified By', modifiedBy)}
                    </Col>
                    <Col xs={24} md={12}>
                        {renderAuditItem(
                            'Modified Date',
                            modifiedDate ? moment(modifiedDate).format('YYYY-MM-DD HH:mm') : ''
                        )}
                    </Col>
                </>
            )}
            <Form.Item name="avatar" />
            <Form.Item name="id" />
            <Form.Item name="companies" />
            <Form.Item name="user" />
            <Form.Item name="image" />
            <Form.Item name="createdby_name" hidden />
            <Form.Item name="created_date" hidden />
            <Form.Item name="lastModified_name" hidden />
            <Form.Item name="modified_date" hidden />
        </Row>
    );
};

export default UserDetailFormFields;
